import cc from 'classcat';
import { useRef, useCallback, useEffect } from 'react';
import { DEFAULT_INPUT_BORDER_WIDTH_IN_EM, BASE_FONT_SIZE, FONT_SIZE_SMALL_IN_EM, FONT_SIZE_MEDIUM_IN_EM, FONT_SIZE_LARGE_IN_EM, FONT_SIZE_EXTRA_LARGE_IN_EM } from '@i-ark/constants';
import { InputTypes } from '@i-ark/models';
import { setAlphaOnColor } from '@i-ark/utils';
import { units } from '@ui/css';
import { scaleContainerCls, fontSizeContainerCls, containerCls } from './Input.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useScale } from '../hooks/useScale.js';

const Input = ({
  sheetRef,
  pageId,
  pageAspectRatio,
  zIndex,
  item,
  isCloned,
  isSelected,
  onSelect,
  onDragStart,
  defaultInputColor,
  Input,
  Before,
  After
}) => {
  const scale = useScale();
  const borderWidth = `${Math.max(1, Math.round(DEFAULT_INPUT_BORDER_WIDTH_IN_EM * BASE_FONT_SIZE * scale)).toString()}px`;
  const ref = useRef(null);
  const handleSelect = useCallback(evt => {
    if (onSelect !== undefined) {
      onSelect(evt, item);
    }
  }, [item, onSelect]);
  useEffect(() => {
    const node = ref.current;
    if (node === null) {
      return;
    }
    node.addEventListener("focusin", handleSelect, {
      passive: false
    });
    node.addEventListener("mousedown", handleSelect, {
      passive: false
    });
    node.addEventListener("touchstart", handleSelect, {
      passive: false
    });
    return () => {
      node.removeEventListener("focusin", handleSelect);
      node.removeEventListener("mousedown", handleSelect);
      node.removeEventListener("touchstart", handleSelect);
    };
  }, [item, handleSelect]);
  let fontSize;
  switch (item.style.font.size) {
    case "extra-large":
      fontSize = `${FONT_SIZE_EXTRA_LARGE_IN_EM.toString()}em`;
      break;
    case "large":
      fontSize = `${FONT_SIZE_LARGE_IN_EM.toString()}em`;
      break;
    case "medium":
      fontSize = `${FONT_SIZE_MEDIUM_IN_EM.toString()}em`;
      break;
    case "small":
      fontSize = `${FONT_SIZE_SMALL_IN_EM.toString()}em`;
      break;
    default:
      fontSize = `${item.style.font.size.toString()}em`;
  }
  const isTransparent = item.style.background.color === "transparent" || typeof item.style.background.opacity === "number" && item.style.background.opacity === 0;
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    "data-drag-target": true,
    "data-id": item.id,
    className: cc([containerCls, {
      multiLine: item.type === InputTypes.TEXTAREA,
      isTransparent
    }]),
    style: {
      zIndex,
      left: `${(item.position.x * 100).toString()}%`,
      top: `${(item.position.y * 100).toString()}%`,
      width: `${(item.size.width * 100).toString()}%`,
      height: `${(item.size.height * 100).toString()}%`,
      backgroundColor: setAlphaOnColor(item.style.background.color, item.style.background.opacity ?? 1),
      border: item.style.border !== undefined ? `${Math.max(1, Math.round(item.style.border.width * BASE_FONT_SIZE * scale)).toString()}px ${item.style.border.style} ${item.style.border.color}` : `min(${borderWidth}, ${units.px(3)}) solid #000`,
      borderRadius: item.style.border?.radius !== undefined ? `${item.style.border.radius.toString()}em` : undefined,
      fontFamily: item.style.font.family,
      color: item.style.font.color ?? defaultInputColor
    },
    children: [Before !== undefined && /*#__PURE__*/jsx(Before, {
      sheetRef: sheetRef,
      pageId: pageId,
      pageAspectRatio: pageAspectRatio,
      isSelected: isSelected,
      isCloned: isCloned,
      item: item
    }), /*#__PURE__*/jsx("div", {
      className: scaleContainerCls,
      children: /*#__PURE__*/jsxs("span", {
        className: fontSizeContainerCls,
        style: {
          fontSize
        },
        children: [/*#__PURE__*/jsx(Input, {
          sheetRef: sheetRef,
          pageId: pageId,
          pageAspectRatio: pageAspectRatio,
          item: item,
          isSelected: isSelected,
          isCloned: isCloned,
          scale: scale
        }), /*#__PURE__*/jsx("span", {})]
      })
    }), After !== undefined && /*#__PURE__*/jsx(After, {
      sheetRef: sheetRef,
      pageId: pageId,
      pageAspectRatio: pageAspectRatio,
      isSelected: isSelected,
      isCloned: isCloned,
      item: item,
      onDragStart: onDragStart
    })]
  });
};

export { Input };
//# sourceMappingURL=Input.js.map
